import { useContext, useLayoutEffect } from 'react'

import { useArcadeMigrationFeatureFlag } from '../ArcadeComponentMigration/ArcadeComponentMigrationProvider'
import { ArcadeModalActionContext, ArcadeModalActionOverride } from './ArcadeModalActionContext'
import { IsInArcadeModalContext } from './ArcadeModalContext'

/**
 * Use this hook to override the actions of an ArcadeModal or ArcadeModalPane.
 *
 * You can either:
 * 1. Override button props (back/close buttons)
 * 2. Provide a completely custom header component
 *
 * @param override - The action override configuration
 *
 * @example
 * ```tsx
 * // Override button props
 * useArcadeModalActionOverride({
 *   type: 'buttons',
 *   backButtonProps: {
 *     onBack: () => console.log('Custom back'),
 *     ariaLabel: 'Go back'
 *   },
 *   closeButtonProps: {
 *     onClose: () => console.log('Custom close'),
 *     ariaLabel: 'Close modal'
 *   }
 * })
 *
 * // Override with custom header
 * useArcadeModalActionOverride({
 *   type: 'customHeader',
 *   CustomHeader: <MyCustomHeader />
 * })
 * ```
 */
export const useArcadeModalActionOverride = (override: ArcadeModalActionOverride) => {
  const isInArcadeModal = useContext(IsInArcadeModalContext)
  const { setActionOverride } = useContext(ArcadeModalActionContext)
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('modal')

  useLayoutEffect(() => {
    // TODO: remove migration flag check when Arcade modal migration is complete
    if (!isArcadeMigrationEnabled) {
      return
    } else if (!isInArcadeModal) {
      throw Error('Override is not within Arcade modal')
    } else {
      setActionOverride(override)
    }
  }, [override, setActionOverride, isInArcadeModal, isArcadeMigrationEnabled])

  useLayoutEffect(() => {
    if (!isArcadeMigrationEnabled || !isInArcadeModal) {
      return
    }
    return () => {
      // Clean up modal action override in a separate useLayoutEffect, so that it only runs on unmount
      setActionOverride(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInArcadeModal])
}
